import type { PartnerAnlegen, PartnerUebersicht } from '@nac2s/shared'
import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Input } from '@/components/base/input/input'
import { Toggle } from '@/components/base/toggle/toggle'
import { Dialogfenster } from '@/teile/Dialogfenster'
import { Knopf } from '@/teile/Knopf'
import { Status } from '@/teile/Status'
import { Fehler, Laedt, Leer, Tabelle } from '@/teile/Tabelle'
import { Zahl } from '@/teile/Zahl'
import { cx } from '@/utils/cx'
import { ApiError } from '../api/client.js'
import { ladeUebersicht, partnerAnlegen } from '../api/verwaltung.js'
import { Seitenkopf } from '../layout/AppShell.js'
import { euro, monatsname } from '../teile/monat.js'

/**
 * Partnerliste — der Einstieg der Verwaltung: je Partner Kunden, Seeds und
 * der Gesamtpreis des laufenden Monats (ADR-0015 Nachtrag). Eine Zeile fuehrt
 * zum Partner. netable steht hier als erster Partner wie jeder andere.
 */

type Ladezustand =
  | { status: 'laedt' }
  | { status: 'da'; monat: string; partner: PartnerUebersicht[] }
  | { status: 'fehler'; meldung: string }

export function Partner() {
  const [zustand, setZustand] = useState<Ladezustand>({ status: 'laedt' })
  const [formularOffen, setFormularOffen] = useState(false)
  const navigate = useNavigate()

  const laden = useCallback(async () => {
    try {
      const { monat, partner } = await ladeUebersicht()
      setZustand({ status: 'da', monat, partner })
    } catch (fehler: unknown) {
      setZustand({
        status: 'fehler',
        meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
      })
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  const alle = zustand.status === 'da' ? zustand.partner : []
  const kunden = alle.reduce((s, p) => s + p.kunden, 0)
  const seeds = alle.reduce((s, p) => s + p.seeds, 0)
  const umsatz = alle.reduce((s, p) => s + p.umsatzCent, 0)
  const gestoert = alle.filter((p) => p.letzterAbruf?.status === 'fehler').length

  return (
    <div>
      <Seitenkopf
        beisatz={zustand.status === 'da' ? monatsname(zustand.monat) : heute()}
        titel="Partner"
        satz="Wer die Plattform betreibt, wie viele Kunden und Seeds er hat, und was der laufende Monat kostet."
        rechts={
          <Knopf stark onClick={() => setFormularOffen(true)}>
            Partner anlegen
          </Knopf>
        }
      />

      <div className="flex flex-wrap gap-6 px-6 pb-4">
        <Kennzahl name="Partner" wert={alle.length} />
        <Kennzahl name="Kunden" wert={kunden} />
        <Kennzahl name="Seeds" wert={seeds} />
        <Kennzahl name="Gesamtpreis laufender Monat" text={euro(umsatz)} />
        <Kennzahl name="Abruf gestört" wert={gestoert} achtung={gestoert > 0} />
      </div>

      <section className="px-6 pb-8">
        {zustand.status === 'laedt' ? (
          <Laedt>Partner werden geladen …</Laedt>
        ) : zustand.status === 'fehler' ? (
          <Fehler>{zustand.meldung}</Fehler>
        ) : alle.length === 0 ? (
          <Leer titel="Noch kein Partner">
            Der erste Partner ist netable selbst — der eigene Stack läuft über dieselbe Verwaltung
            wie jeder fremde.
          </Leer>
        ) : (
          <Tabelle kopf={['Partner', 'Kunden', 'Seeds', 'Gesamtpreis', 'Stack', '']}>
            {alle.map((p) => (
              <tr
                key={p.id}
                onClick={() => navigate(`/partner/${p.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/partner/${p.id}`)
                }}
                tabIndex={0}
                className="cursor-pointer border-b border-secondary transition-colors last:border-b-0 hover:bg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-akzent"
              >
                <td className="py-2.5 pr-2.5">
                  <span className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className={cx(
                        'grid size-[30px] shrink-0 place-items-center rounded-[10px] font-display text-xs font-bold tracking-tight',
                        zeichenFarbe(p),
                      )}
                    >
                      {kuerzel(p.name)}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-primary">
                        {p.name}
                      </span>
                      <span className="block truncate text-[11.5px] text-quaternary">
                        {p.billingRef}
                        {p.kundenDomain ? ` · *.${p.kundenDomain}` : ''}
                      </span>
                    </span>
                  </span>
                </td>
                <td className="py-2.5 pr-2.5 text-primary tabular-nums">{p.kunden}</td>
                <td className="py-2.5 pr-2.5 text-primary tabular-nums">{p.seeds}</td>
                <td className="py-2.5 pr-2.5 tabular-nums">
                  <span className="font-semibold text-primary">{euro(p.umsatzCent)}</span>
                  {p.abweichungen > 0 ? (
                    <span className="ml-2">
                      <Status ton="wartet">Sonderpreis</Status>
                    </span>
                  ) : null}
                </td>
                <td className="py-2.5 pr-2.5">
                  <StackStatus p={p} />
                </td>
                <td className="py-2.5 text-right">
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                    className="inline size-[13px] text-quaternary"
                  >
                    <path
                      d="M6 3l5 5-5 5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </td>
              </tr>
            ))}
          </Tabelle>
        )}
      </section>

      {formularOffen ? (
        <PartnerFormular
          onAbbruch={() => setFormularOffen(false)}
          onAngelegt={(id) => {
            setFormularOffen(false)
            void laden()
            void navigate(`/partner/${id}`)
          }}
        />
      ) : null}
    </div>
  )
}

function StackStatus({ p }: { p: PartnerUebersicht }) {
  if (p.zustand !== 'aktiv') {
    return <Status ton="ruhig">{p.zustand === 'gesperrt' ? 'gesperrt' : 'gekündigt'}</Status>
  }
  if (!p.stackAngebunden) return <Status ton="wartet">nicht angebunden</Status>
  if (!p.letzterAbruf) return <Status ton="wartet">noch kein Abruf</Status>
  if (p.letzterAbruf.status === 'fehler') return <Status ton="stoerung">Abruf gestört</Status>
  return <Status ton="gut">antwortet</Status>
}

function zeichenFarbe(p: PartnerUebersicht): string {
  if (p.zustand !== 'aktiv') return 'bg-secondary text-tertiary'
  if (p.letzterAbruf?.status === 'fehler') return 'bg-error-primary text-fg-error-primary'
  if (!p.stackAngebunden) return 'bg-achtung-flaeche text-achtung-tief'
  return 'bg-success-primary text-fg-success-primary'
}

function Kennzahl({
  name,
  wert,
  text,
  achtung,
}: {
  name: string
  wert?: number
  text?: string
  achtung?: boolean
}) {
  return (
    <div>
      <p className="text-xs text-tertiary">{name}</p>
      <p
        className={cx(
          'font-display text-xl font-bold tabular-nums',
          achtung ? 'text-achtung-tief' : 'text-primary',
        )}
      >
        {text ?? <Zahl wert={wert ?? 0} />}
      </p>
    </div>
  )
}

function heute(): string {
  return new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })
}

export function kuerzel(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((teil) => teil[0])
    .join('')
    .toUpperCase()
}

/** Anlegen: Name, Abrechnungsbezug und Subdomain sind Pflicht, der Stack kann später kommen. */
function PartnerFormular({
  onAbbruch,
  onAngelegt,
}: {
  onAbbruch: () => void
  onAngelegt: (id: string) => void
}) {
  const [name, setName] = useState('')
  const [billingRef, setBillingRef] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [stackHost, setStackHost] = useState('')
  const [stackEndpoint, setStackEndpoint] = useState('')
  const [autoApprove, setAutoApprove] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const subdomainGueltig = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(subdomain.trim())

  async function absenden(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    setLaeuft(true)
    setMeldung(null)
    const eingabe: PartnerAnlegen = {
      name: name.trim(),
      billingRef: billingRef.trim(),
      autoApprove,
      whiteLabel: false,
      baseDomain: null,
      subdomain: subdomain.trim(),
      stackHost: stackHost.trim() || null,
      stackEndpoint: stackEndpoint.trim() || null,
      notizen: null,
    }
    try {
      onAngelegt((await partnerAnlegen(eingabe)).id)
    } catch (fehler: unknown) {
      setMeldung(fehler instanceof ApiError ? fehler.message : 'Der Partner wurde nicht angelegt.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Dialogfenster
      titel="Partner anlegen"
      satz="Der Stack kann später gekoppelt werden — Adresse und Kopplungscode stehen dann in den Einstellungen des Partners."
      onSchliessen={onAbbruch}
    >
      <form onSubmit={absenden} className="flex flex-col gap-4 pb-6">
        <Input isRequired label="Name" size="md" value={name} onChange={setName} />
        <Input
          isRequired
          label="Abrechnungsbezug"
          hint="Kennung im Rechnungssystem (Autotask, weclapp)."
          size="md"
          value={billingRef}
          onChange={setBillingRef}
        />
        <Input
          isRequired
          label="Subdomain"
          hint="Unter der Plattform-Domain: Kundenverwaltung unter <subdomain>.<plattform>, Kunden unter <kunde>.<subdomain>.<plattform>."
          size="md"
          value={subdomain}
          onChange={(w) => setSubdomain(w.toLowerCase())}
          placeholder="z. B. netabletest"
        />
        <Input
          label="Stack-Host"
          hint="Server, auf dem der Stack läuft — nur zur Übersicht."
          size="md"
          value={stackHost}
          onChange={setStackHost}
        />
        <Input
          label="Stack-Adresse"
          hint="Interne Adresse der Partner-API, z. B. http://api:3000."
          size="md"
          value={stackEndpoint}
          onChange={setStackEndpoint}
          placeholder="http://"
        />
        <Toggle
          label="Aufträge automatisch freigeben"
          hint="Für bewährte Partner und netable selbst. Jederzeit abschaltbar."
          isSelected={autoApprove}
          onChange={setAutoApprove}
        />
        {meldung ? <Fehler>{meldung}</Fehler> : null}
        <div className="flex justify-end gap-2">
          <Knopf onClick={onAbbruch} disabled={laeuft}>
            Abbrechen
          </Knopf>
          <Knopf
            type="submit"
            stark
            disabled={laeuft || !name.trim() || !billingRef.trim() || !subdomainGueltig}
          >
            Anlegen
          </Knopf>
        </div>
      </form>
    </Dialogfenster>
  )
}
